import { t } from '../ui/lang';
import { esc } from '../ui/modal';
const HEIGHT = 320;
const PAD = { top: 16, right: 96, bottom: 28, left: 48 };
const Y_TICKS = 5;
/** Minimum horizontal room per x label before labels are skipped. */
const X_LABEL_MIN_PX = 64;
/** Minimum vertical gap between two direct end labels. */
const END_LABEL_GAP = 14;
function niceMax(v) {
    if (v <= Y_TICKS)
        return Y_TICKS;
    const raw = v / Y_TICKS;
    const pow = 10 ** Math.floor(Math.log10(raw));
    const step = [1, 2, 2.5, 5, 10].map((m) => m * pow).find((s) => s >= raw) ?? raw;
    return step * Y_TICKS;
}
const fmtN = (n) => n.toLocaleString();
/**
 * Draw the chart into `host` (replacing its content). `hidden` holds series keys toggled
 * off in the legend; `onToggle` is called with a key when its legend item is clicked.
 */
export function renderLineChart(host, data, hidden, onToggle) {
    const width = Math.max(320, host.clientWidth || 800);
    const n = data.xLabels.length;
    const shown = data.series.filter((s) => !hidden.has(s.key));
    const maxV = niceMax(Math.max(0, ...shown.flatMap((s) => s.values)));
    const plotW = width - PAD.left - PAD.right;
    const plotH = HEIGHT - PAD.top - PAD.bottom;
    const x = (i) => PAD.left + (n <= 1 ? plotW / 2 : (i / (n - 1)) * plotW);
    const y = (v) => PAD.top + plotH - (v / maxV) * plotH;
    const grid = [];
    for (let k = 0; k <= Y_TICKS; k += 1) {
        const v = (maxV / Y_TICKS) * k;
        const yy = y(v);
        grid.push(`<line class="viz__grid${k === 0 ? ' viz__grid--base' : ''}" x1="${PAD.left}" x2="${width - PAD.right}" y1="${yy}" y2="${yy}"/>`);
        grid.push(`<text class="viz__tick" x="${PAD.left - 8}" y="${yy + 4}" text-anchor="end">${fmtN(Math.round(v))}</text>`);
    }
    const every = Math.max(1, Math.ceil(n / Math.max(1, Math.floor(plotW / X_LABEL_MIN_PX))));
    const xTicks = data.xLabels.map((label, i) => (i % every === 0
        ? `<text class="viz__tick" x="${x(i)}" y="${HEIGHT - 8}" text-anchor="middle">${esc(label)}</text>` : '')).join('');
    const lines = shown.map((s) => {
        const d = s.values.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join('');
        // A single point draws no segment, so it gets a dot instead.
        const dot = n === 1 ? `<circle cx="${x(0)}" cy="${y(s.values[0] ?? 0)}" r="4" fill="${s.color}"/>` : '';
        return `<path class="viz__line${s.dashed ? ' viz__line--dashed' : ''}" d="${d}" style="stroke:${s.color}"/>${dot}`;
    }).join('');
    // Direct labels at the right end, pushed apart so they never overlap.
    const ends = shown.map((s) => ({ s, y: y(s.values[n - 1] ?? 0) })).sort((a, b) => a.y - b.y);
    for (let i = 1; i < ends.length; i += 1) {
        if (ends[i].y - ends[i - 1].y < END_LABEL_GAP)
            ends[i].y = ends[i - 1].y + END_LABEL_GAP;
    }
    const endLabels = n ? ends.map(({ s, y: ly }) => `<text class="viz__end" x="${width - PAD.right + 8}" y="${ly + 4}">${esc(s.label)}</text>`).join('') : '';
    const legend = data.series.map((s) => {
        const total = s.values.reduce((a, b) => a + b, 0);
        const off = hidden.has(s.key);
        return `<button type="button" class="viz__legend-item${off ? ' is-off' : ''}" data-key="${esc(s.key)}" aria-pressed="${off ? 'false' : 'true'}" title="${esc(t('charts.legend_toggle'))}">
            <svg class="viz__swatch" viewBox="0 0 20 8" aria-hidden="true"><line x1="1" y1="4" x2="19" y2="4" style="stroke:${s.color}"${s.dashed ? ' stroke-dasharray="4 3"' : ''}/></svg>
            <span>${esc(s.label)}</span><b>${fmtN(total)}</b>
        </button>`;
    }).join('');
    host.innerHTML = `
        <div class="viz__legend">${legend}</div>
        <div class="viz__plot">
            <svg class="viz__svg" width="${width}" height="${HEIGHT}" viewBox="0 0 ${width} ${HEIGHT}" role="img">
                ${grid.join('')}${xTicks}${lines}${endLabels}
                <g class="viz__hover" style="display:none"><line class="viz__crosshair" y1="${PAD.top}" y2="${PAD.top + plotH}"/><g class="viz__dots"></g></g>
                <rect class="viz__hit" x="${PAD.left}" y="${PAD.top}" width="${plotW}" height="${plotH}" fill="transparent"/>
            </svg>
            <div class="viz__tooltip" style="display:none"></div>
        </div>
    `;
    host.querySelectorAll('.viz__legend-item').forEach((b) => {
        b.addEventListener('click', () => onToggle(b.dataset['key']));
    });
    if (!n)
        return;
    const svg = host.querySelector('.viz__svg');
    const hover = host.querySelector('.viz__hover');
    const cross = host.querySelector('.viz__crosshair');
    const dots = host.querySelector('.viz__dots');
    const tip = host.querySelector('.viz__tooltip');
    const hit = host.querySelector('.viz__hit');
    hit.addEventListener('mousemove', (ev) => {
        const box = svg.getBoundingClientRect();
        const px = ev.clientX - box.left;
        const i = n <= 1 ? 0 : Math.max(0, Math.min(n - 1, Math.round(((px - PAD.left) / plotW) * (n - 1))));
        const cx = x(i);
        cross.setAttribute('x1', String(cx));
        cross.setAttribute('x2', String(cx));
        dots.innerHTML = shown.map((s) => `<circle class="viz__dot" cx="${cx}" cy="${y(s.values[i] ?? 0)}" r="4" fill="${s.color}"/>`).join('');
        hover.style.display = '';
        const total = shown.reduce((a, s) => a + (s.values[i] ?? 0), 0);
        tip.innerHTML = `<div class="viz__tooltip-title">${esc(data.xTitles[i] ?? '')}</div>`
            + shown.map((s) => `<div class="viz__tooltip-row"><svg class="viz__swatch" viewBox="0 0 20 8" aria-hidden="true"><line x1="1" y1="4" x2="19" y2="4" style="stroke:${s.color}"${s.dashed ? ' stroke-dasharray="4 3"' : ''}/></svg><span>${esc(s.label)}</span><b>${fmtN(s.values[i] ?? 0)}</b></div>`).join('')
            + `<div class="viz__tooltip-row viz__tooltip-total"><span>${esc(t('charts.total'))}</span><b>${fmtN(total)}</b></div>`;
        tip.style.display = '';
        // Flip to the left of the crosshair when the tooltip would run off the plot.
        const tipW = tip.offsetWidth;
        tip.style.left = `${cx + 12 + tipW > width ? cx - 12 - tipW : cx + 12}px`;
        tip.style.top = `${PAD.top}px`;
    });
    hit.addEventListener('mouseleave', () => { hover.style.display = 'none'; tip.style.display = 'none'; });
}
/** The same data as a table — the accessible view of the chart. */
export function renderLineTable(host, data, periodLabel) {
    const head = `<th>${esc(periodLabel)}</th>${data.series.map((s) => `<th>${esc(s.label)}</th>`).join('')}<th>${esc(t('charts.total'))}</th>`;
    const rows = data.xTitles.map((title, i) => {
        const vals = data.series.map((s) => s.values[i] ?? 0);
        return `<tr><td>${esc(title)}</td>${vals.map((v) => `<td>${fmtN(v)}</td>`).join('')}<td><b>${fmtN(vals.reduce((a, b) => a + b, 0))}</b></td></tr>`;
    }).reverse().join('');
    host.innerHTML = `<div class="clicker-ops-table-wrap"><table class="access-matrix viz__table"><thead><tr>${head}</tr></thead><tbody>${rows}</tbody></table></div>`;
}
